import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  ArrowLeft,
  Link2,
  MapPinned,
} from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from './SearchableSelect';
import PhotoUploader from './PhotoUploader';
import { ALL_STATES, getDistrictsForState } from './data/indianStatesDistricts';
import { getEvent, updateEvent } from '../apis/ride-24';

const apiError = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.message || fallback;

const difficultyOptions = [
  { value: 'easy', label: 'Easy' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'challenging', label: 'Challenging' },
  { value: 'expert', label: 'Expert' },
];

const bikeTypeOptions = [
  { value: 'any', label: 'Any' },
  { value: 'cruiser', label: 'Cruiser' },
  { value: 'sport', label: 'Sport / Supersport' },
  { value: 'touring', label: 'Touring' },
  { value: 'adventure', label: 'Adventure / ADV' },
  { value: 'dirt', label: 'Dirt / Off-road' },
  { value: 'naked', label: 'Naked / Streetfighter' },
  { value: 'cafe_racer', label: 'Cafe Racer' },
  { value: 'scooter', label: 'Scooter' },
];

const inputDark =
  'bg-gray-950 border-gray-700 text-gray-100 placeholder:text-gray-500 focus-visible:ring-red-600 focus-visible:ring-offset-0';

const EditEvent = ({ user }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [formData, setFormData] = useState(null);

  const fetchEvent = useCallback(async () => {
    try {
      const { data } = await getEvent(eventId);
      const ev = data.event;
      if (ev.organizer_id !== user.id && user.role !== 'admin') {
        toast.error('Only the organiser can edit this ride');
        navigate(`/ride-24/events/${eventId}`);
        return;
      }
      if (ev.status === 'cancelled') {
        toast.error('A cancelled ride cannot be edited');
        navigate(`/ride-24/events/${eventId}`);
        return;
      }
      const d = new Date(ev.date);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      setFormData({
        title: ev.title,
        description: ev.description,
        date: d,
        time: `${hh}:${mm}`,
        start_point: ev.start_point,
        end_point: ev.end_point,
        google_maps_link: ev.google_maps_link || '',
        state: ev.state,
        district: ev.district,
        difficulty_level: ev.difficulty_level,
        bike_type: ev.bike_type,
        max_participants: String(ev.max_participants),
        photos: ev.photos || [],
        gender_restriction: ev.gender_restriction || 'anyone',
      });
    } catch (err) {
      toast.error('Failed to load ride');
      navigate('/ride-24');
    } finally {
      setLoading(false);
    }
  }, [eventId, user.id, user.role, navigate]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  const update = (patch) => setFormData((f) => ({ ...f, ...patch }));

  const stateOptions = ALL_STATES.map((s) => ({ value: s, label: s }));
  const districtOptions = getDistrictsForState(formData?.state).map((d) => ({
    value: d,
    label: d,
  }));

  const isValid = () =>
    formData &&
    formData.title.trim() &&
    formData.description.trim() &&
    formData.date &&
    formData.time &&
    formData.start_point.trim() &&
    formData.end_point.trim() &&
    formData.state &&
    formData.district &&
    formData.difficulty_level &&
    formData.bike_type &&
    formData.max_participants &&
    parseInt(formData.max_participants, 10) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid()) return;
    setSaving(true);
    try {
      const [hh, mm] = formData.time.split(':');
      const eventDateTime = new Date(formData.date);
      eventDateTime.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);

      const payload = {
        title: formData.title,
        description: formData.description,
        date: eventDateTime.toISOString(),
        start_point: formData.start_point,
        end_point: formData.end_point,
        google_maps_link: formData.google_maps_link.trim() || null,
        state: formData.state,
        district: formData.district,
        difficulty_level: formData.difficulty_level,
        bike_type: formData.bike_type,
        max_participants: parseInt(formData.max_participants, 10),
        photos: formData.photos,
        gender_restriction: formData.gender_restriction,
      };
      await updateEvent(eventId, payload);
      toast.success('Ride updated');
      navigate(`/ride-24/events/${eventId}`);
    } catch (err) {
      toast.error(apiError(err, 'Failed to update ride'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !formData) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="h-8 w-40 bg-zinc-900 rounded animate-pulse mb-6" />
          <div className="h-96 bg-zinc-900 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center mb-8">
          <Button
            onClick={() => navigate(`/ride-24/events/${eventId}`)}
            variant="ghost"
            className="mr-4 p-2 text-gray-300 hover:text-white hover:bg-white/5"
            data-testid="edit-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1
              className="text-3xl font-bold text-white"
              data-testid="edit-event-title"
            >
              Edit Ride
            </h1>
            <p className="text-gray-400 mt-2">
              Update your ride details. Riders who already RSVP&apos;d will see the new info.
            </p>
          </div>
        </div>

        <Card className="bg-gray-900/80 border border-gray-800 shadow-xl text-gray-200">
          <CardHeader>
            <CardTitle className="text-white">Ride Details</CardTitle>
            <CardDescription className="text-gray-400">
              Edit the information about your motorcycle ride
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="title" className="text-gray-200">
                    Ride Title *
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => update({ title: e.target.value })}
                    className={inputDark}
                    required
                    data-testid="edit-title-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="description" className="text-gray-200">
                    Description *
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => update({ description: e.target.value })}
                    rows={4}
                    className={inputDark}
                    required
                    data-testid="edit-description-input"
                  />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label className="text-gray-200">Date *</Label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${inputDark} hover:bg-gray-900 hover:text-gray-100`}
                        data-testid="edit-date-input"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                        {formData.date ? format(formData.date, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-gray-900 border border-gray-800 text-gray-200"
                      align="start"
                    >
                      <CalendarComponent
                        mode="single"
                        selected={formData.date}
                        onSelect={(d) => {
                          update({ date: d });
                          setDatePickerOpen(false);
                        }}
                        disabled={(d) => d < today}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="time" className="text-gray-200">
                    Time *
                  </Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10 pointer-events-none" />
                    <Input
                      id="time"
                      type="time"
                      value={formData.time}
                      onChange={(e) => update({ time: e.target.value })}
                      className={`pl-10 ${inputDark} [color-scheme:dark]`}
                      required
                      data-testid="edit-time-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor="start_point" className="text-gray-200">
                    Start Point *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-red-500" />
                    <Input
                      id="start_point"
                      value={formData.start_point}
                      onChange={(e) => update({ start_point: e.target.value })}
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="edit-start-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="end_point" className="text-gray-200">
                    End Point *
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-red-500" />
                    <Input
                      id="end_point"
                      value={formData.end_point}
                      onChange={(e) => update({ end_point: e.target.value })}
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="edit-end-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="google_maps_link" className="text-gray-200">
                  Google Maps Route Link (optional)
                </Label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="google_maps_link"
                    type="url"
                    value={formData.google_maps_link}
                    onChange={(e) => update({ google_maps_link: e.target.value })}
                    className={`pl-10 ${inputDark}`}
                    data-testid="edit-maps-link-input"
                  />
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label className="text-gray-200">State *</Label>
                  <div className="relative">
                    <MapPinned className="absolute left-3 top-3 h-4 w-4 text-red-500 z-10 pointer-events-none" />
                    <SearchableSelect
                      value={formData.state}
                      onChange={(val) => update({ state: val, district: null })}
                      options={stateOptions}
                      placeholder="Select state / UT"
                      testId="edit-state-select"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-200">District *</Label>
                  <div className="relative">
                    <MapPinned className="absolute left-3 top-3 h-4 w-4 text-red-500 z-10 pointer-events-none" />
                    <SearchableSelect
                      value={formData.district}
                      onChange={(val) => update({ district: val })}
                      options={districtOptions}
                      placeholder={formData.state ? 'Select district' : 'Pick a state first'}
                      disabled={!formData.state}
                      testId="edit-district-select"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-gray-200">Ride photos</Label>
                <PhotoUploader
                  photos={formData.photos}
                  onChange={(photos) => update({ photos })}
                />
              </div>

              <div>
                <Label className="text-gray-200">Who can join *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'anyone', label: 'Anyone', hint: 'Open to all riders' },
                    { value: 'male_only', label: 'Male only', hint: 'Male riders only' },
                    { value: 'female_only', label: 'Female only', hint: 'Female riders only' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update({ gender_restriction: opt.value })}
                      className={`p-3 rounded-md border-2 text-left transition-colors ${
                        formData.gender_restriction === opt.value
                          ? 'border-red-600 bg-red-950/30 text-white'
                          : 'border-gray-700 bg-gray-950 text-gray-300 hover:border-gray-600'
                      }`}
                      data-testid={`edit-gender-${opt.value}`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-3">
                <div>
                  <Label className="text-gray-200">Difficulty Level *</Label>
                  <Select
                    value={formData.difficulty_level}
                    onValueChange={(v) => update({ difficulty_level: v })}
                  >
                    <SelectTrigger className={inputDark} data-testid="edit-difficulty-select">
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-gray-900 border border-gray-800 text-gray-200">
                      {difficultyOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="focus:bg-red-600 focus:text-white"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-200">Ride Type *</Label>
                  <Select
                    value={formData.bike_type}
                    onValueChange={(v) => update({ bike_type: v })}
                  >
                    <SelectTrigger className={inputDark} data-testid="edit-bike-type-select">
                      <SelectValue placeholder="Select ride type" />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-gray-900 border border-gray-800 text-gray-200">
                      {bikeTypeOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="focus:bg-red-600 focus:text-white"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="max_participants" className="text-gray-200">
                    Max Participants *
                  </Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="max_participants"
                      type="number"
                      value={formData.max_participants}
                      onChange={(e) => update({ max_participants: e.target.value })}
                      min="1"
                      max="1000"
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="edit-max-participants-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/ride-24/events/${eventId}`)}
                  className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
                  data-testid="edit-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid() || saving}
                  className="btn-hover bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
                  data-testid="edit-submit-button"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditEvent;
